import { useMemo } from 'react';
import dayjs from 'dayjs';

import {
  Alert,
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';

import {
  CancelDeleteUserButton
} from 'src/components/Button';
import CalendarField from 'src/components/CalendarField';

import {
  PAUSE_DURATION_OPTIONS,
  PauseDurationValue
} from './constants';
import { PauseDurationStepProps } from './types';

const PauseDurationStep = ({
  selectedDuration,
  pauseUntilDate,
  onDurationChange,
  onPauseUntilDateChange,
  onBack,
  onConfirmPause,
  isLoading
}: PauseDurationStepProps) => {
  const isCustomDuration = selectedDuration === PauseDurationValue.CUSTOM;

  const isConfirmDisabled = useMemo(
    () => isLoading || (isCustomDuration && !pauseUntilDate),
    [isCustomDuration, isLoading, pauseUntilDate]
  );

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h3" mb={1}>
          {/* How long would you like to pause? */}
          Review and Confirm Pause
        </Typography>
        <Typography color="text.secondary" variant="body1">
          Your data and settings will be preserved while your subscription is
          paused.
        </Typography>
      </Box>

      <RadioGroup
        name="pause-duration"
        onChange={(event) =>
          onDurationChange(event.target.value as PauseDurationValue)
        }
        value={selectedDuration}
      >
        {PAUSE_DURATION_OPTIONS.map((option) => (
          <Box
            border={1}
            borderColor={
              selectedDuration === option.value ? 'primary.main' : 'divider'
            }
            borderRadius={2}
            key={option.value}
            mb={2}
            px={2}
            py={1}
          >
            <FormControlLabel
              control={<Radio />}
              label={option.label}
              sx={{ mr: 0, width: '100%' }}
              value={option.value}
            />
          </Box>
        ))}
      </RadioGroup>

      <Collapse in={isCustomDuration}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2}
          mt={1}
        >
          <CalendarField
            containerStyle={{ flex: 1 }}
            disablePast
            label="Pause Until Date"
            labelStyle={{
              display: 'block',
              fontWeight: 700,
              marginBottom: 8
            }}
            onChange={onPauseUntilDateChange}
            preventManual
            textFieldProps={{
              disabled: isLoading,
              fullWidth: true,
              size: 'small'
            }}
            minDate={dayjs().add(1, 'day').startOf('day')}
            value={pauseUntilDate}
          />
        </Box>
      </Collapse>

      <Collapse in={isCustomDuration}>
        <Box>
          <Alert
            severity="info"
            style={{
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}
            sx={{
              mb: 2,
              mt: 2,
              fontWeight: 600,
              minWidth: 0,
              alignSelf: 'stretch'
            }}
          >
            Your subscription will be paused from now until the selected date.
          </Alert>
        </Box>
      </Collapse>

      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={2}
        mt={3}
      >
        <Button disabled={isLoading} fullWidth onClick={onBack} variant="outlined">
          Back
        </Button>
        <CancelDeleteUserButton
          disabled={isConfirmDisabled}
          fullWidth
          onClick={onConfirmPause}
        >
          Confirm Pause
        </CancelDeleteUserButton>
      </Box>
    </Box>
  );
};

export default PauseDurationStep;
